import { datadogLogs } from '@datadog/browser-logs';
import { EVENT_USER_LANGUAGE_CHANGED, EVENT_USER_PROFILE_CHANGED } from '@rio-cloud/rio-user-menu-component';
import type { UserManager } from 'oidc-client-ts';

import { store } from '#store/store.ts';

import { config } from '../config.ts';
import { extractLanguage } from './lang/lang.ts';
import { getLocale } from './lang/langSlice.ts';
import { configureFetchDisplayMessages } from './lang/services.ts';
import {
    configureMockUserManager,
    configureUserManager,
    createUserManager,
    type SessionRenewedResult,
} from './login/login.ts';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from './login/loginSlice.ts';
import { completeLoginRedirect, showLoginRedirectError } from './login/redirect.ts';
import { attemptInitialSignIn } from './setup/oauth.ts';
import { reportErrorToSentry } from './setup/sentry.ts';
import { trace } from './setup/trace.ts';
import { accessToken } from './tokenHandling/accessToken.ts';
import { accessTokenStored, idTokenStored } from './tokenHandling/tokenSlice.ts';

export interface OAuthConfig {
    onSessionExpired: () => void;
    onSessionRenewed: (result: SessionRenewedResult) => void;
}

export const main = async (renderApp: () => void, isLoginRedirect = false) => {
    const fetchDisplayMessages = configureFetchDisplayMessages(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    store.subscribe(() => {
        const lang = extractLanguage(getLocale(store.getState()));
        const html = document.querySelector('html');

        if (html && lang && html.getAttribute('lang') !== lang) {
            html.setAttribute('lang', lang);
        }
    });

    const oauthConfig = {
        onSessionExpired: () => {
            accessToken.discardAccessToken();
            store.dispatch(userSessionExpired());
        },
        onSessionRenewed: (result: SessionRenewedResult) => {
            trace('index.onTokenRenewed', result);

            accessToken.saveAccessToken(result.accessToken);
            store.dispatch(accessTokenStored(result.accessToken));
            store.dispatch(idTokenStored(result.idToken));
            store.dispatch(userProfileObtained(result.profile));

            datadogLogs.setGlobalContextProperty('customer', result.profile.account);

            store.dispatch(userSessionRenewed());

            // You will need to get the user language yourself then
            // you may fetch the suitable messages. Depending
            // on when and from where you fetch the user settings you might
            // want to employ a loading spinner while the request is ongoing.
            fetchDisplayMessages(result.locale);
        },
    } as OAuthConfig;

    const userManager: UserManager = config.login.mockAuthorization
        ? configureMockUserManager(oauthConfig)
        : configureUserManager(oauthConfig, createUserManager());

    const signinSilent = userManager.signinSilent.bind(userManager);
    document.addEventListener(EVENT_USER_LANGUAGE_CHANGED, () => signinSilent());
    document.addEventListener(EVENT_USER_PROFILE_CHANGED, () => signinSilent());

    try {
        await userManager.clearStaleState();
        if (isLoginRedirect) {
            await completeLoginRedirect(userManager);
        } else {
            await attemptInitialSignIn(userManager);
        }
        renderApp();
    } catch (error) {
        if (isLoginRedirect) {
            showLoginRedirectError();
        }
        trace('could not start application', error);
        reportErrorToSentry(error);
    }
};
